import { keepPreviousData } from '@tanstack/react-query';
import { match } from 'ts-pattern';

import Empty from 'components/Empty';
import FrameGrid from 'components/FrameGrid';
import FrameGridInfiniteSkeletonLoader from 'components/FrameGridInfiniteSkeletonLoader';
import NftGridItem from 'components/NftGridItem';
import Box from 'components/base/Box';
import Flex from 'components/base/Flex';
import NftsSort from 'components/filters/NftsSort';
import MarketAvailabilityFilter from 'components/filters/api/MarketAvailabilityFilter';
import FilterLayout from 'components/profiles/tabs/FilterLayout';
import FiltersSkeleton from 'components/profiles/tabs/FiltersSkeleton';

import { useInfiniteNftsSearch } from 'gql/api/queries/nfts-search.generated';
import { useFilterStateAtom } from 'hooks/filters/use-nft-filters';
import { HASURA_DEFAULT_ITEMS_PER_PAGE } from 'lib/constants';
import { mapApiMediaToPreviewMediaAsset } from 'utils/media-preview';
import { apiPaginator } from 'utils/react-query';

type ListingsTabProps = {
  listingsCount: number;
  worldId: number;
};

const MAX_COLUMNS = 4;

export default function ListingsTab(props: ListingsTabProps) {
  const { worldId: id, listingsCount } = props;
  const LISTINGS_TAB_CACHE_KEY = `LISTINGS_TAB_${id}`;

  const filtersApi = useFilterStateAtom({
    cacheKey: LISTINGS_TAB_CACHE_KEY,
    initialValues: {
      sortOrder: 'MOST_ACTIVE',
    },
  });

  const infiniteNftsSearchQuery = useInfiniteNftsSearch(
    {
      searchTerm: '',
      exhibitionId: id,
      accountAddress: filtersApi.filters.creatorPublicKey,
      perPage: HASURA_DEFAULT_ITEMS_PER_PAGE,
      page: 0,
      marketAvailability: filtersApi.filters.marketAvailability,
      sortOrder: filtersApi.filters.sortOrder,
    },
    {
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        if (lastPage.nftsSearchV2Db.search.items.length === 0) {
          return null;
        }
        return {
          page: lastPage.nftsSearchV2Db.search.page + 1,
        };
      },
      initialPageParam: apiPaginator.initialPageParam,
    }
  );

  if (!infiniteNftsSearchQuery.isSuccess) {
    return (
      <FilterLayout filters={<FiltersSkeleton buttons={1} />}>
        <FrameGrid.Root maxColumns={4}>
          {Array.from({ length: Math.min(listingsCount, 8) }).map(
            (_, index) => {
              return <NftGridItem.Skeleton key={index} />;
            }
          )}
        </FrameGrid.Root>
      </FilterLayout>
    );
  }

  const nfts = match(infiniteNftsSearchQuery)
    .with({ status: 'success' }, ({ data }) =>
      data.pages.flatMap((page) => page.nftsSearchV2Db.search.items)
    )
    .otherwise(() => []);

  return (
    <FilterLayout
      filters={
        <Flex
          css={{
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '$2',
          }}
        >
          <MarketAvailabilityFilter
            filters={filtersApi.filters}
            setMarketAvailability={filtersApi.setMarketAvailability}
          />
          <Box css={{ marginLeft: 'auto' }}>
            <NftsSort
              filters={filtersApi.filters}
              setFilters={filtersApi.setFilters}
            />
          </Box>
        </Flex>
      }
    >
      {nfts.length === 0 ? (
        <Empty.WithFilters
          message="Adjust the selected filters to see more NFTs."
          onClear={() => {
            filtersApi.resetFilters();
          }}
        />
      ) : (
        <FrameGrid.Root
          maxColumns={MAX_COLUMNS}
          css={{
            transition: 'opacity $2 $ease',
            opacity: infiniteNftsSearchQuery.isRefetching ? 0.5 : 1,
          }}
        >
          {nfts.map((nft) => {
            const media = nft.media
              ? mapApiMediaToPreviewMediaAsset(nft.media)
              : null;

            const optimizedMedia = media
              ? NftGridItem.optimizeMedia(media, {
                  maxColumns: MAX_COLUMNS,
                })
              : null;

            return (
              <NftGridItem
                key={nft.id}
                nft={nft}
                media={optimizedMedia}
                filterContext={
                  filtersApi.filters.marketAvailability || undefined
                }
              />
            );
          })}

          {infiniteNftsSearchQuery.hasNextPage && (
            <FrameGridInfiniteSkeletonLoader
              fetchNextPage={infiniteNftsSearchQuery.fetchNextPage}
              loadedItems={nfts.length}
              maxColumns={MAX_COLUMNS}
              skeleton={<NftGridItem.Skeleton />}
              totalItems={undefined}
            />
          )}
        </FrameGrid.Root>
      )}
    </FilterLayout>
  );
}
